import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { products } from "../data/productsData";
import Button from "./Button";
import SectionTitle from "./SectionTitle";
import { Github, ExternalLink, Sparkles, CheckCircle2, Dumbbell, Smartphone, Info, X } from "lucide-react";

const getIcon = (iconName) => {
  switch (iconName) {
    case "Dumbbell":
      return <Dumbbell className="w-6 h-6 text-indigo-500" />;
    case "Smartphone":
      return <Smartphone className="w-6 h-6 text-purple-500" />;
    default:
      return <Sparkles className="w-6 h-6 text-indigo-500" />;
  }
};

const Products = () => {
  const [selectedProduct, setSelectedProduct] = useState(null);

  return (
    <section id="products" className="py-24 bg-white dark:bg-gray-950 relative overflow-hidden">
      {/* Ambient background glow */}
      <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-10 right-10 w-[400px] h-[400px] bg-indigo-500/10 rounded-full blur-3xl pointer-events-none"></div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <SectionTitle
          title="My Products & Mobile Apps"
          subtitle="Independent cross-platform applications, SaaS products, and mobile tools built for scale"
        />

        {/* Products Grid - Image-Free Glass Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
          {products.map((product) => (
            <motion.div
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              key={product.id}
              className="group bg-gradient-to-b from-white to-gray-50/80 dark:from-gray-900 dark:to-gray-950 rounded-3xl p-6 sm:p-8 border border-gray-200/80 dark:border-gray-800 shadow-xl hover:shadow-2xl hover:shadow-indigo-500/10 transition-all duration-500 flex flex-col justify-between relative overflow-hidden"
            >
              {/* Top Accent Gradient Border */}
              <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${product.accentColor}`}></div>

              <div>
                {/* Header Icon & Status Pill Bar */}
                <div className="flex items-center justify-between gap-4 mb-5">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-2xl bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-100 dark:border-indigo-800/60 flex items-center justify-center shadow-inner">
                      {getIcon(product.iconName)}
                    </div>
                    <div>
                      <span className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400">
                        {product.badge}
                      </span>
                      <h3 className="text-2xl font-bold font-heading text-gray-900 dark:text-white leading-tight">
                        {product.title}
                      </h3>
                    </div>
                  </div>

                  {product.metric && (
                    <span className="shrink-0 text-xs font-bold px-3 py-1 rounded-full bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200/60 dark:border-emerald-800/60 shadow-sm">
                      ⚡ {product.metric}
                    </span>
                  )}
                </div>

                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  {product.subtitle}
                </p>

                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                  {product.description}
                </p>

                {/* Highlights Summary */}
                <div className="mb-6 space-y-2">
                  {product.highlights.slice(0, 3).map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-2 text-xs text-gray-600 dark:text-gray-300">
                      <CheckCircle2 className="w-3.5 h-3.5 text-indigo-500 shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>

                {/* Tech Stack Pills */}
                <div className="flex flex-wrap gap-2 mb-8">
                  {product.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-100 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300 border border-gray-200/60 dark:border-gray-700/60"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-gray-200/60 dark:border-gray-800">
                {product.projectUrl && product.projectUrl.startsWith("http") && !product.projectUrl.includes("github.com") && (
                  <Button
                    variant="primary"
                    href={product.projectUrl}
                    className="py-2.5 px-4 text-xs font-bold shadow-md flex-1"
                  >
                    <span>View Live App</span>
                    <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
                  </Button>
                )}

                <Button
                  variant="secondary"
                  href={product.githubUrl}
                  className="py-2.5 px-4 text-xs font-bold flex-1"
                >
                  <Github className="w-3.5 h-3.5 mr-1.5" />
                  <span>GitHub</span>
                </Button>

                <button
                  onClick={() => setSelectedProduct(product)}
                  className="p-2.5 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 transition-colors cursor-pointer"
                  title="View Details"
                >
                  <Info className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedProduct && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white dark:bg-gray-900 rounded-3xl max-w-xl w-full p-6 sm:p-8 relative border border-gray-200 dark:border-gray-800 shadow-2xl overflow-y-auto max-h-[90vh]"
            >
              <button
                onClick={() => setSelectedProduct(null)}
                className="absolute top-5 right-5 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="flex items-center space-x-2 text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-widest mb-2">
                <span>{selectedProduct.badge}</span>
                {selectedProduct.metric && (
                  <span className="bg-emerald-50 dark:bg-emerald-950 px-2 py-0.5 rounded-full text-emerald-700 dark:text-emerald-300">
                    ⚡ {selectedProduct.metric}
                  </span>
                )}
              </div>

              <h3 className="text-2xl font-bold font-heading text-gray-900 dark:text-white mb-2">
                {selectedProduct.title}
              </h3>
              <p className="text-sm font-semibold text-gray-600 dark:text-gray-300 mb-4">
                {selectedProduct.subtitle}
              </p>

              <p className="text-sm text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                {selectedProduct.description}
              </p>

              <div className="mb-6">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Key Technical Architecture</h4>
                <div className="space-y-2.5">
                  {selectedProduct.highlights.map((h, i) => (
                    <div key={i} className="flex items-start space-x-2.5 text-xs text-gray-700 dark:text-gray-200">
                      <Sparkles className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" />
                      <span>{h}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-8">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Tech Stack</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedProduct.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full text-xs font-semibold bg-indigo-50 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                {selectedProduct.projectUrl && selectedProduct.projectUrl.startsWith("http") && !selectedProduct.projectUrl.includes("github.com") && (
                  <Button variant="primary" href={selectedProduct.projectUrl} className="flex-1 py-3 text-xs font-bold">
                    <span>View Live App</span>
                    <ExternalLink className="w-3.5 h-3.5 ml-2" />
                  </Button>
                )}
                <Button variant="secondary" href={selectedProduct.githubUrl} className="flex-1 py-3 text-xs font-bold">
                  <Github className="w-3.5 h-3.5 mr-2" />
                  <span>GitHub Repository</span>
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Products;
